import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { format, subDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { RefreshCw, Cpu, Zap } from 'lucide-react';

interface TokenUsageRow {
  user_id: string;
  provider: string;
  tokens_input: number;
  tokens_output: number;
  model: string | null;
  edge_function: string | null;
  created_at: string;
}

// Tarifs par million de tokens
const PRICING = {
  perplexity: { input: 3, output: 15 },    // Sonar Pro $/M
  openrouter: { input: 0.14, output: 0.28 }, // DeepSeek V3 $/M
};

const estimateCost = (provider: 'perplexity' | 'openrouter', input: number, output: number) => {
  const p = PRICING[provider];
  return (input * p.input + output * p.output) / 1_000_000;
};

interface UserTokenSummary {
  user_id: string;
  email: string;
  perplexity_input: number;
  perplexity_output: number;
  openrouter_input: number;
  openrouter_output: number;
  total: number;
  cost: number;
}

export const TokenUsageStats = () => {
  const [period, setPeriod] = useState<'today' | '7d' | '30d'>('today');
  const [loading, setLoading] = useState(true);
  const [summaries, setSummaries] = useState<UserTokenSummary[]>([]);
  const [totalPerplexity, setTotalPerplexity] = useState({ input: 0, output: 0 });
  const [totalOpenrouter, setTotalOpenrouter] = useState({ input: 0, output: 0 });

  const fetchData = async () => {
    setLoading(true);
    try {
      const now = new Date();
      let startDate: Date;
      switch (period) {
        case 'today':
          startDate = new Date(now.toISOString().split('T')[0]);
          break;
        case '7d':
          startDate = subDays(now, 7);
          break;
        case '30d':
          startDate = subDays(now, 30);
          break;
      }

      const { data: usageData, error } = await supabase
        .from('token_usage')
        .select('*')
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erreur chargement token usage:', error);
        setSummaries([]);
        return;
      }

      // Fetch user emails
      const userIds = [...new Set((usageData || []).map((r: any) => r.user_id))];
      const { data: profiles } = await supabase
        .from('profils_utilisateur')
        .select('id, email')
        .in('id', userIds);

      const emailMap: Record<string, string> = {};
      profiles?.forEach((p: any) => { emailMap[p.id] = p.email || 'N/A'; });

      // Aggregate per user
      const userMap: Record<string, UserTokenSummary> = {};
      let pIn = 0, pOut = 0, oIn = 0, oOut = 0;

      (usageData || []).forEach((row: any) => {
        if (!userMap[row.user_id]) {
          userMap[row.user_id] = {
            user_id: row.user_id,
            email: emailMap[row.user_id] || 'N/A',
            perplexity_input: 0, perplexity_output: 0,
            openrouter_input: 0, openrouter_output: 0,
            total: 0, cost: 0,
          };
        }
        const u = userMap[row.user_id];
        if (row.provider === 'perplexity') {
          u.perplexity_input += row.tokens_input;
          u.perplexity_output += row.tokens_output;
          pIn += row.tokens_input;
          pOut += row.tokens_output;
        } else if (row.provider === 'openrouter') {
          u.openrouter_input += row.tokens_input;
          u.openrouter_output += row.tokens_output;
          oIn += row.tokens_input;
          oOut += row.tokens_output;
        }
        u.total += row.tokens_input + row.tokens_output;
        u.cost = estimateCost('perplexity', u.perplexity_input, u.perplexity_output) +
                 estimateCost('openrouter', u.openrouter_input, u.openrouter_output);
      });

      setTotalPerplexity({ input: pIn, output: pOut });
      setTotalOpenrouter({ input: oIn, output: oOut });
      setSummaries(Object.values(userMap).sort((a, b) => b.total - a.total));
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [period]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-nautic flex items-center gap-2">
          <Cpu className="h-6 w-6" />
          Consommation IA (Tokens)
        </h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Actualiser
          </Button>
          <Select value={period} onValueChange={(v) => setPeriod(v as any)}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Aujourd'hui</SelectItem>
              <SelectItem value="7d">7 derniers jours</SelectItem>
              <SelectItem value="30d">30 derniers jours</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Zap className="h-4 w-4 text-blue-500" />
              Perplexity (Sonar)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{(totalPerplexity.input + totalPerplexity.output).toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              Input: {totalPerplexity.input.toLocaleString()} | Output: {totalPerplexity.output.toLocaleString()}
            </p>
            <p className="text-sm font-semibold text-primary mt-1">
              ~${estimateCost('perplexity', totalPerplexity.input, totalPerplexity.output).toFixed(4)}
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Zap className="h-4 w-4 text-green-500" />
              OpenRouter (DeepSeek)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{(totalOpenrouter.input + totalOpenrouter.output).toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              Input: {totalOpenrouter.input.toLocaleString()} | Output: {totalOpenrouter.output.toLocaleString()}
            </p>
            <p className="text-sm font-semibold text-primary mt-1">
              ~${estimateCost('openrouter', totalOpenrouter.input, totalOpenrouter.output).toFixed(4)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Per-user table */}
      <Card>
        <CardHeader>
          <CardTitle>Détail par utilisateur</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <RefreshCw className="h-4 w-4 animate-spin mr-2" />
              Chargement...
            </div>
          ) : summaries.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Aucune donnée de consommation pour cette période.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Utilisateur</TableHead>
                   <TableHead className="text-right">Perplexity (in/out)</TableHead>
                   <TableHead className="text-right">OpenRouter (in/out)</TableHead>
                   <TableHead className="text-right">Total tokens</TableHead>
                   <TableHead className="text-right">Coût estimé</TableHead>
                 </TableRow>
               </TableHeader>
               <TableBody>
                 {summaries.map((s) => (
                   <TableRow key={s.user_id}>
                     <TableCell className="font-medium text-sm">{s.email}</TableCell>
                     <TableCell className="text-right text-sm">
                       {s.perplexity_input + s.perplexity_output > 0 ? (
                         <span>{s.perplexity_input.toLocaleString()} / {s.perplexity_output.toLocaleString()}</span>
                       ) : (
                         <span className="text-muted-foreground">—</span>
                       )}
                     </TableCell>
                     <TableCell className="text-right text-sm">
                       {s.openrouter_input + s.openrouter_output > 0 ? (
                         <span>{s.openrouter_input.toLocaleString()} / {s.openrouter_output.toLocaleString()}</span>
                       ) : (
                         <span className="text-muted-foreground">—</span>
                       )}
                     </TableCell>
                     <TableCell className="text-right font-bold">{s.total.toLocaleString()}</TableCell>
                     <TableCell className="text-right font-semibold text-primary">${s.cost.toFixed(4)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
